import { useCallback, useReducer } from 'react';
import { redactBase64FromAiPrompt } from './htmlAiRequest.js';

export const AI_TASK_TYPES = Object.freeze({
  GENERATE_PAGE: 'generate-page',
  GLOBAL_EDIT: 'global-edit',
  FOCUSED_EDIT: 'focused-edit',
  ADD_SECTION: 'add-section',
  SCORE_PORTFOLIO: 'score-portfolio',
  IMPLEMENT_RECOMMENDATION: 'implement-recommendation',
  REGENERATE_PAGE: 'regenerate-page',
  REFINE_PREVIEW: 'refine-preview',
});

export const AI_WORKFLOW_PHASES = Object.freeze({
  DESCRIBE: 'describe',
  CONFIRM: 'confirm',
  WORKING: 'working',
  REVIEW: 'review',
  REFINE: 'refine',
  DONE: 'done',
  ERROR: 'error',
  CONVERSATION: 'conversation',
});

const PROMPT_TASKS = new Set([
  AI_TASK_TYPES.GENERATE_PAGE,
  AI_TASK_TYPES.GLOBAL_EDIT,
  AI_TASK_TYPES.FOCUSED_EDIT,
  AI_TASK_TYPES.ADD_SECTION,
  AI_TASK_TYPES.REGENERATE_PAGE,
]);

export const isGuidedAiTask = (taskType) => (
  taskType !== AI_TASK_TYPES.SCORE_PORTFOLIO
  && Boolean(taskType)
);

export const initialPhaseForAiTask = (taskType) => {
  if (taskType === AI_TASK_TYPES.SCORE_PORTFOLIO) {
    return AI_WORKFLOW_PHASES.CONVERSATION;
  }
  return PROMPT_TASKS.has(taskType)
    ? AI_WORKFLOW_PHASES.DESCRIBE
    : AI_WORKFLOW_PHASES.WORKING;
};

export const AI_TASK_DETAILS = Object.freeze({
  [AI_TASK_TYPES.GENERATE_PAGE]: {
    label: 'Create portfolio',
    placeholder: 'Describe the portfolio page you would like to create...',
    promptRequired: true,
  },
  [AI_TASK_TYPES.GLOBAL_EDIT]: {
    label: 'Edit portfolio',
    placeholder: 'Describe the changes you would like to make...',
    promptRequired: true,
  },
  [AI_TASK_TYPES.FOCUSED_EDIT]: {
    label: 'Edit selected item',
    placeholder: 'Describe how this item should change...',
    promptRequired: true,
  },
  [AI_TASK_TYPES.ADD_SECTION]: {
    label: 'Add a section',
    placeholder: 'Describe the section you would like to add...',
    promptRequired: true,
  },
  [AI_TASK_TYPES.SCORE_PORTFOLIO]: {
    label: 'Score portfolio',
    placeholder: '',
    promptRequired: false,
  },
  [AI_TASK_TYPES.IMPLEMENT_RECOMMENDATION]: {
    label: 'Apply recommendation',
    placeholder: '',
    promptRequired: false,
  },
  [AI_TASK_TYPES.REGENERATE_PAGE]: {
    label: 'Regenerate portfolio',
    placeholder: 'Describe the replacement portfolio page...',
    promptRequired: true,
  },
  [AI_TASK_TYPES.REFINE_PREVIEW]: {
    label: 'Revise preview',
    placeholder: 'Describe what should be different in the proposed version...',
    promptRequired: true,
  },
});

let nextMessageId = 0;
let nextRunId = 0;
const messageId = () => `ai-message-${++nextMessageId}`;
const runId = () => `ai-run-${++nextRunId}`;

const taskKey = (task = {}) => [
  task.type,
  task.nodeId,
  task.targetId,
  task.position,
  task.previewId,
  task.scoreMessageId,
  task.recommendationIndex,
  task.recommendation?.title,
].filter(Boolean).join(':');

export const createAiTask = (type, context = {}) => ({
  ...context,
  type,
  key: taskKey({ type, ...context }),
});

export const createAssistantMessage = (role, content, options = {}) => ({
  id: messageId(),
  role,
  ...options,
  content: String(redactBase64FromAiPrompt(String(content || '')) || ''),
});

export const initialAiAssistantState = {
  open: false,
  activeTask: null,
  activeRun: null,
  draft: '',
  messages: [],
  runs: [],
};

const taskMessage = (task, currentRunId) => createAssistantMessage(
  'task',
  task?.label || AI_TASK_DETAILS[task?.type]?.label || 'AI task',
  { taskType: task?.type, runId: currentRunId }
);

const messagesWithActiveTask = (messages, task, currentRunId) => {
  const nextTaskMessage = taskMessage(task, currentRunId);
  if (messages[messages.length - 1]?.role === 'task') {
    return [...messages.slice(0, -1), nextTaskMessage];
  }
  return [...messages, nextTaskMessage];
};

const createTaskRun = (task) => ({
  id: runId(),
  task,
  phase: initialPhaseForAiTask(task?.type),
  submittedRequest: '',
  previewId: null,
  outcome: '',
  error: '',
});

const replaceRun = (runs, nextRun) => [
  ...runs.filter((run) => run.id !== nextRun.id),
  nextRun,
];

const updateActiveRun = (state, updates) => {
  if (!state.activeRun) return state;
  const activeRun = { ...state.activeRun, ...updates };
  return {
    ...state,
    activeRun,
    runs: replaceRun(state.runs, activeRun),
  };
};

export const aiAssistantReducer = (state, action) => {
  switch (action.type) {
    case 'open-task': {
      const sameTask = Boolean(
        !action.restart
        && state.activeTask?.key === action.task?.key
        && state.activeRun?.phase !== AI_WORKFLOW_PHASES.DONE
      );
      const activeRunHasContent = state.messages.some((message) => (
        message.runId === state.activeRun?.id && message.role !== 'task'
      ));
      const replaceUnusedRun = Boolean(
        state.activeRun
        && state.activeRun.phase === AI_WORKFLOW_PHASES.DESCRIBE
        && !state.activeRun.submittedRequest
        && !activeRunHasContent
      );
      const currentRun = sameTask ? state.activeRun : createTaskRun(action.task);
      const priorRuns = sameTask
        ? state.runs
        : state.runs
          .filter((run) => !replaceUnusedRun || run.id !== state.activeRun?.id)
          .map((run) => run.id === state.activeRun?.id && run.phase !== AI_WORKFLOW_PHASES.DONE
            ? { ...run, phase: AI_WORKFLOW_PHASES.DONE, outcome: run.outcome || 'Completed' }
            : run);
      const priorMessages = replaceUnusedRun && !sameTask
        ? state.messages.filter((message) => message.runId !== state.activeRun?.id)
        : state.messages;
      return {
        ...state,
        open: true,
        activeTask: action.task,
        activeRun: currentRun,
        draft: action.draft ?? (sameTask ? state.draft : ''),
        messages: sameTask
          ? priorMessages
          : messagesWithActiveTask(priorMessages, action.task, currentRun.id),
        runs: sameTask ? priorRuns : [...priorRuns, currentRun],
      };
    }
    case 'close':
      return { ...state, open: false };
    case 'reopen':
      return { ...state, open: true };
    case 'set-draft':
      return { ...state, draft: action.value };
    case 'set-active-task':
      return { ...state, activeTask: action.task, draft: action.draft ?? state.draft };
    case 'retarget-focused-task': {
      const canRetarget = Boolean(
        action.task?.type === AI_TASK_TYPES.FOCUSED_EDIT
        && state.activeTask?.type === AI_TASK_TYPES.FOCUSED_EDIT
        && state.activeRun?.phase === AI_WORKFLOW_PHASES.DESCRIBE
        && !state.activeRun.submittedRequest
      );
      if (!canRetarget || state.activeTask.key === action.task.key) return state;

      const activeRun = { ...state.activeRun, task: action.task };
      return {
        ...state,
        activeTask: action.task,
        activeRun,
        runs: replaceRun(state.runs, activeRun),
        messages: state.messages.map((message) => (
          message.runId === activeRun.id && message.role === 'task'
            ? taskMessage(action.task, activeRun.id)
            : message
        )),
      };
    }
    case 'set-phase':
      return updateActiveRun(state, {
        phase: action.phase,
        ...(action.submittedRequest !== undefined
          ? { submittedRequest: action.submittedRequest }
          : {}),
        ...(action.previewId !== undefined ? { previewId: action.previewId } : {}),
        ...(action.error !== undefined ? { error: action.error } : {}),
      });
    case 'complete-run': {
      if (!state.activeRun) return state;
      const completedRun = {
        ...state.activeRun,
        phase: AI_WORKFLOW_PHASES.DONE,
        outcome: action.outcome || 'Completed',
        error: '',
        previewId: null,
      };
      return {
        ...state,
        activeTask: null,
        activeRun: null,
        draft: '',
        runs: replaceRun(state.runs, completedRun),
      };
    }
    case 'add-message': {
      const message = action.message.runId || !state.activeRun
        ? action.message
        : { ...action.message, runId: state.activeRun.id };
      return { ...state, messages: [...state.messages, message] };
    }
    case 'mark-recommendation-applied':
      return {
        ...state,
        messages: state.messages.map((message) => {
          if (message.id !== action.scoreMessageId || message.payload?.kind !== 'score') {
            return message;
          }
          const recommendations = message.payload.score?.recommendations;
          if (!Array.isArray(recommendations) || !recommendations[action.recommendationIndex]) {
            return message;
          }
          return {
            ...message,
            payload: {
              ...message.payload,
              score: {
                ...message.payload.score,
                recommendations: recommendations.map((recommendation, index) => (
                  index === action.recommendationIndex
                    ? { ...recommendation, applied: true }
                    : recommendation
                )),
              },
            },
          };
        }),
      };
    case 'clear-draft':
      return { ...state, draft: '' };
    case 'return-to-review': {
      const reviewState = updateActiveRun(state, {
        phase: AI_WORKFLOW_PHASES.REVIEW,
        error: '',
      });
      return {
        ...reviewState,
        activeTask: reviewState.activeTask
          ? { ...reviewState.activeTask, showComposer: false }
          : null,
        draft: '',
      };
    }
    case 'hydrate-history':
      return {
        ...state,
        activeTask: null,
        activeRun: null,
        draft: '',
        runs: Array.isArray(action.history?.runs) ? action.history.runs : [],
        messages: Array.isArray(action.history?.messages) ? action.history.messages : [],
      };
    case 'reset':
      return initialAiAssistantState;
    default:
      return state;
  }
};

export const useAiAssistantState = () => {
  const [state, dispatch] = useReducer(aiAssistantReducer, initialAiAssistantState);
  const openTask = useCallback((task, options = {}) => {
    dispatch({
      type: 'open-task',
      task,
      draft: options.draft,
      restart: options.restart,
    });
  }, []);
  const close = useCallback(() => dispatch({ type: 'close' }), []);
  const reopen = useCallback(() => dispatch({ type: 'reopen' }), []);
  const setDraft = useCallback((value) => dispatch({ type: 'set-draft', value }), []);
  const setActiveTask = useCallback((task, options = {}) => {
    dispatch({ type: 'set-active-task', task, draft: options.draft });
  }, []);
  const retargetFocusedTask = useCallback((task) => {
    dispatch({ type: 'retarget-focused-task', task });
  }, []);
  const setPhase = useCallback((phase, options = {}) => {
    dispatch({ type: 'set-phase', phase, ...options });
  }, []);
  const completeRun = useCallback((outcome) => {
    dispatch({ type: 'complete-run', outcome });
  }, []);
  const clearDraft = useCallback(() => dispatch({ type: 'clear-draft' }), []);
  const returnToReview = useCallback(() => dispatch({ type: 'return-to-review' }), []);
  const hydrateHistory = useCallback((history) => {
    dispatch({ type: 'hydrate-history', history });
  }, []);
  const reset = useCallback(() => dispatch({ type: 'reset' }), []);
  const addMessage = useCallback((role, content, options = {}) => {
    dispatch({ type: 'add-message', message: createAssistantMessage(role, content, options) });
  }, []);
  const markRecommendationApplied = useCallback((scoreMessageId, recommendationIndex) => {
    dispatch({
      type: 'mark-recommendation-applied',
      scoreMessageId,
      recommendationIndex,
    });
  }, []);

  return {
    ...state,
    openTask,
    close,
    reopen,
    setDraft,
    setActiveTask,
    retargetFocusedTask,
    setPhase,
    completeRun,
    clearDraft,
    returnToReview,
    hydrateHistory,
    reset,
    addMessage,
    markRecommendationApplied,
  };
};
